import { createClient } from '@supabase/supabase-js'

// Environment variables — warn loudly if missing to prevent silent misconfiguration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    'Missing Supabase environment variables. Ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set. SECRET features will not work.'
  )
}

// Create Supabase client with SECRET-specific configuration
// Falls back to placeholder values when env vars are missing so public pages still load
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-anon-key',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce'
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
)

// Database Table Names (with secret_ prefix)
export const TABLES = {
  USERS: 'secret_users',
  EVENTS: 'secret_events',
  MENU_ITEMS: 'secret_menu_items',
  ORDERS: 'secret_orders',
  ORDER_ITEMS: 'secret_order_items',
  NOTIFICATIONS: 'secret_notifications',
  TRANSFER_LOGS: 'secret_transfer_logs',
  ADMIN_USERS: 'secret_admin_users',
  VERIFICATION_CODES: 'secret_verification_codes'
} as const

// Type definitions for database tables
export interface SecretUser {
  id: string
  email?: string
  phone?: string
  email_verified: boolean
  phone_verified: boolean
  first_name?: string
  last_name?: string
  zip_code?: string
  latitude?: number
  longitude?: number
  notification_preferences: {
    sms: boolean
    email: boolean
    push: boolean
  }
  created_at: string
  updated_at: string
  last_login?: string
  last_sign_in?: string  // Current sign-in timestamp
  previous_sign_in?: string  // Previous sign-in timestamp - null means first visit
  is_active: boolean
}

export interface SecretEvent {
  id: string
  title: string
  description?: string
  image_url?: string | null  // Hero image for event card
  location_name: string
  address: string
  latitude: number
  longitude: number
  zip_code: string
  event_date: string
  notification_date: string
  registration_opens: string
  registration_closes: string
  pickup_start_time?: string | null  // Service/pickup window start
  pickup_end_time?: string | null    // Service/pickup window end
  max_orders?: number
  current_orders: number
  max_lobster_tails?: number | null  // Phase 2: Total lobster tail capacity
  max_tails_per_order?: number | null  // Phase 2: Per-order tail limit
  status: 'draft' | 'scheduled' | 'active' | 'completed' | 'cancelled'
  created_at: string
  updated_at: string
  created_by: string
}

export interface SecretMenuItem {
  id: string
  event_id: string
  name: string
  description?: string
  price: number
  image_url?: string
  is_secret_exclusive: boolean
  max_quantity: number
  current_quantity: number
  is_available: boolean
  order_index: number  // Display order for menu items
  lobster_tail_qty: number  // Phase 2: Number of lobster tails in this menu item
  created_at: string
}

export interface SecretOrder {
  id: string
  user_id: string
  event_id: string
  order_number: string
  total_amount: number
  square_payment_id?: string
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'completed' | 'cancelled' | 'transferred'
  pickup_time?: string
  qr_code_url?: string
  transfer_token?: string
  transfer_expires_at?: string
  transferred_to?: string
  transferred_at?: string
  created_at: string
  updated_at: string
}

export interface SecretOrderItem {
  id: string
  order_id: string
  menu_item_id: string
  quantity: number
  unit_price: number
  total_price: number
}

export interface SecretNotification {
  id: string
  user_id: string
  event_id?: string
  type: 'event_announcement' | 'order_confirmation' | 'order_status' | 'transfer_received' | 'system'
  title: string
  message: string
  channels: ('sms' | 'email' | 'push')[]
  sent_at?: string
  read_at?: string
  created_at: string
}

export interface SecretTransferLog {
  id: string
  order_id: string
  from_user_id: string
  to_user_id?: string
  to_contact: string
  transfer_token: string
  status: 'pending' | 'claimed' | 'expired' | 'cancelled'
  claimed_at?: string
  expires_at: string
  created_at: string
}

export interface SecretAdminUser {
  id: string
  user_id: string
  role: 'admin' | 'manager' | 'staff'
  permissions: string[]
  created_at: string
}

export interface SecretVerificationCode {
  id: string
  user_id: string
  code: string
  type: 'email' | 'sms'
  contact: string
  expires_at: string
  used_at?: string
  created_at: string
}

// Auth helpers
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error) throw error
  return user
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

// User management functions
export const createSecretUser = async (userData: Partial<SecretUser>) => {
  // First attempt: Direct insert (should work with new auth timing fix)
  try {
    const { data, error } = await supabase
      .from(TABLES.USERS)
      .insert([userData])
      .select()
      .single()
    
    if (error) throw error
    return data
  } catch (error: any) {
    // If RLS policy fails, wait and retry once to handle auth timing issues
    if (error.message?.includes('row-level security policy')) {
      console.log('Auth timing issue detected, retrying user creation...')
      
      // Wait for auth session to propagate
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Retry the insert
      const { data, error: retryError } = await supabase
        .from(TABLES.USERS)
        .insert([userData])
        .select()
        .single()
      
      if (retryError) {
        console.error('User creation failed on retry:', retryError)
        throw new Error(`Failed to create user profile: ${retryError.message}. This may be due to auth session timing. Please try signing up again.`)
      }
      
      return data
    }
    
    // Re-throw other errors
    throw error
  }
}

export const getSecretUser = async (userId: string): Promise<SecretUser | null> => {
  const { data, error } = await supabase
    .from(TABLES.USERS)
    .select('*')
    .eq('id', userId)
    .single()

  if (error && error.code !== 'PGRST116') throw error

  if (import.meta.env.DEV && data) {
    console.log('[getSecretUser] User verification status:', {
      userId: data.id,
      email_verified: data.email_verified,
      phone_verified: data.phone_verified
    })
  }

  return data
}

export const updateSecretUser = async (userId: string, updates: Partial<SecretUser>) => {
  const { data, error } = await supabase
    .from(TABLES.USERS)
    .update(updates)
    .eq('id', userId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

// Event functions
export const getActiveEvents = async (): Promise<SecretEvent[]> => {
  const { data, error } = await supabase
    .from(TABLES.EVENTS)
    .select('*')
    .eq('status', 'active')
    .order('event_date', { ascending: true })
  
  if (error) throw error
  return data || []
}

export const getEvent = async (eventId: string): Promise<SecretEvent | null> => {
  const { data, error } = await supabase
    .from(TABLES.EVENTS)
    .select('*')
    .eq('id', eventId)
    .single()
  
  if (error && error.code !== 'PGRST116') throw error
  return data
}

export const getEventMenuItems = async (eventId: string): Promise<SecretMenuItem[]> => {
  const { data, error } = await supabase
    .from(TABLES.MENU_ITEMS)
    .select('*')
    .eq('event_id', eventId)
    .eq('is_available', true)
    .order('order_index', { ascending: true })

  if (error) throw error
  return data || []
}

// Order functions
export const getUserOrders = async (userId: string): Promise<SecretOrder[]> => {
  const { data, error } = await supabase
    .from(TABLES.ORDERS)
    .select(`
      *,
      secret_events (
        title,
        location_name,
        event_date
      )
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
  
  if (error) throw error
  return data || []
}

/**
 * @deprecated SECURITY WARNING: This function performs a direct INSERT without capacity validation or row-level locking.
 * It is vulnerable to race conditions and can lead to overselling.
 *
 * USE INSTEAD: Call /.netlify/functions/place-order which uses place_order_with_capacity_check() RPC
 * The RPC function provides:
 * - Row-level locking (FOR UPDATE) to prevent race conditions
 * - Atomic capacity validation
 * - Per-order limit validation
 * - Transactional order + order_items creation
 *
 * This function is kept only for backward compatibility with admin tools.
 * DO NOT use for customer-facing order placement.
 */
export const createOrder = async (_orderData: Partial<SecretOrder>) => {
  throw new Error(
    '[DEPRECATED] createOrder() uses direct INSERT and is NOT SAFE for production. ' +
    'Use /.netlify/functions/place-order instead.'
  )
}

/**
 * @deprecated This function should only be used in conjunction with createOrder() (also deprecated).
 * The secure approach is to use place_order_with_capacity_check() RPC which handles both order and items atomically.
 */
export const createOrderItems = async (orderItems: Partial<SecretOrderItem>[]) => {
  const { data, error } = await supabase
    .from(TABLES.ORDER_ITEMS)
    .insert(orderItems)
    .select()

  if (error) throw error
  return data
}

export const updateOrder = async (orderId: string, updates: Partial<SecretOrder>) => {
  const { data, error } = await supabase
    .from(TABLES.ORDERS)
    .update(updates)
    .eq('id', orderId)
    .select()
    .single()

  if (error) throw error
  return data
}

// Notification functions
export const getUserNotifications = async (userId: string): Promise<SecretNotification[]> => {
  const { data, error } = await supabase
    .from(TABLES.NOTIFICATIONS)
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
  
  if (error) throw error
  return data || []
}

export const markNotificationAsRead = async (notificationId: string) => {
  const { data, error } = await supabase
    .from(TABLES.NOTIFICATIONS)
    .update({ read_at: new Date().toISOString() })
    .eq('id', notificationId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

// Real-time subscriptions
export const subscribeToOrderUpdates = (orderId: string, callback: (order: SecretOrder) => void) => {
  return supabase
    .channel(`order:${orderId}`)
    .on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: TABLES.ORDERS,
      filter: `id=eq.${orderId}`
    }, (payload) => {
      callback(payload.new as SecretOrder)
    })
    .subscribe()
}

export const subscribeToUserNotifications = (userId: string, callback: (notification: SecretNotification) => void) => {
  return supabase
    .channel(`notifications:${userId}`)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: TABLES.NOTIFICATIONS,
      filter: `user_id=eq.${userId}`
    }, (payload) => {
      callback(payload.new as SecretNotification)
    })
    .subscribe()
}

// Error handling helper
export const handleSupabaseError = (error: any): string => {
  if (error.message.includes('JWT')) {
    return 'Your session has expired. Please log in again.'
  }
  
  if (error.message.includes('duplicate key')) {
    return 'This information is already registered.'
  }
  
  if (error.message.includes('not found')) {
    return 'The requested item was not found.'
  }
  
  return error.message || 'An unexpected error occurred.'
}

export default supabase